import { useEffect, useState } from "react";
import { UsersThree, Plus, Trash, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";

export default function Organization() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [inviteEmail, setInviteEmail] = useState({});
  const [inviteRole, setInviteRole] = useState({});

  const load = async () => {
    const { data } = await api.get("/orgs");
    setOrgs(data.items);
  };

  useEffect(() => {
    load();
  }, []);

  const createOrg = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/orgs", { name: newName });
      toast.success("Organisation created");
      setNewName("");
      await load();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setCreating(false);
    }
  };

  const invite = async (orgId) => {
    const email = inviteEmail[orgId];
    const role = inviteRole[orgId] || "member";
    if (!email) return;
    try {
      await api.post(`/orgs/${orgId}/invite`, { email, role });
      toast.success("Member added");
      setInviteEmail({ ...inviteEmail, [orgId]: "" });
      await load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const remove = async (orgId, userId) => {
    if (!window.confirm("Remove this member?")) return;
    try {
      await api.delete(`/orgs/${orgId}/members/${userId}`);
      await load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Workspace · RBAC"
        title="Organisations"
        subtitle="Run CarbonIQ as a team. 4-seat packs include roles, shared bookmarks, and the Procurement Lock-in module."
        guideId="organization"
      />

      <section className="px-8 pt-8 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <form onSubmit={createOrg} className="border theme-border theme-surface p-6 space-y-3" data-testid="org-create-form">
          <div className="overline">New organisation</div>
          <h3 className="font-serif-display text-xl theme-ink">Create</h3>
          <input
            data-testid="org-name-input"
            required
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Studio · Atelier · Firm"
            className="w-full theme-input border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-gold"
          />
          <button
            type="submit"
            disabled={creating}
            data-testid="org-create-btn"
            className="w-full bg-gold text-black font-medium py-2.5 rounded-sm hover:bg-[#e0bc40] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Plus size={16} weight="bold" /> {creating ? "Creating…" : "Create org"}
          </button>
          <div className="text-xs theme-ink-dim leading-relaxed pt-1">
            You become the owner. Subscribe to an Org plan from the Billing page to unlock all 4 seats.
          </div>
        </form>

        <div className="xl:col-span-2 space-y-4">
          {orgs.map((org) => (
            <div key={org.id} className="border theme-border theme-surface">
              <div className="px-6 py-4 border-b theme-border flex items-baseline justify-between">
                <div>
                  <div className="overline flex items-center gap-2"><UsersThree size={12} weight="fill" /> {org.members.length}/{org.seat_limit || 4} seats</div>
                  <h3 className="font-serif-display text-2xl theme-ink mt-1">{org.name}</h3>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-black/[0.02] text-[10px] uppercase tracking-[0.2em] theme-ink-dim">
                  <tr>
                    <th className="text-left px-6 py-3">Member</th>
                    <th className="text-left px-3 py-3">Email</th>
                    <th className="text-left px-3 py-3">Role</th>
                    <th className="text-left px-3 py-3">Joined</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {org.members.map((m) => (
                    <tr key={m.user_id} className="border-t theme-border">
                      <td className="px-6 py-3 theme-ink">{m.name}</td>
                      <td className="px-3 theme-ink-muted">{m.email}</td>
                      <td className="px-3">
                        <span className="text-[10px] uppercase tracking-widest border theme-border px-2 py-0.5">
                          {m.role}
                        </span>
                      </td>
                      <td className="px-3 font-mono-data theme-ink-dim">{m.joined_at?.slice(0, 10)}</td>
                      <td className="px-3 text-right">
                        {m.role !== "owner" && org.owner_id === user?.id && (
                          <button onClick={() => remove(org.id, m.user_id)} className="theme-ink-dim hover:text-risk">
                            <Trash size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {org.owner_id === user?.id && org.members.length < (org.seat_limit || 4) && (
                <div className="px-6 py-4 border-t theme-border flex flex-wrap items-center gap-2">
                  <EnvelopeSimple size={14} className="text-gold" />
                  <input
                    data-testid={`invite-email-${org.id}`}
                    value={inviteEmail[org.id] || ""}
                    onChange={(e) => setInviteEmail({ ...inviteEmail, [org.id]: e.target.value })}
                    placeholder="teammate@firm.com (must already have an account)"
                    className="flex-1 min-w-[200px] theme-input border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
                  />
                  <select
                    value={inviteRole[org.id] || "member"}
                    onChange={(e) => setInviteRole({ ...inviteRole, [org.id]: e.target.value })}
                    className="theme-input border px-3 py-2 text-sm rounded-sm"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <button
                    data-testid={`invite-btn-${org.id}`}
                    onClick={() => invite(org.id)}
                    className="bg-gold text-black px-4 py-2 text-sm rounded-sm hover:bg-[#e0bc40]"
                  >
                    Invite
                  </button>
                </div>
              )}
            </div>
          ))}
          {orgs.length === 0 && (
            <div className="border theme-border theme-surface p-12 text-center theme-ink-dim">
              No organisations yet — create one to start collaborating.
            </div>
          )}
        </div>
      </section>
      <div className="pb-12" />
    </div>
  );
}
